import UnityCondensedBold from 'assets/fonts/TCCC-UnityCondensed-Bold.woff2'
import UnityCondensedMedium from 'assets/fonts/TCCC-UnityCondensed-Medium.woff2'
import UnityHeadlineBold from 'assets/fonts/TCCC-UnityHeadline-Bold.woff2'
import UnityHeadlineMedium from 'assets/fonts/TCCC-UnityHeadline-Medium.woff2'
import UnityTextMedium from 'assets/fonts/TCCC-UnityText-Medium.woff2'
import UnityTextRegular from 'assets/fonts/TCCC-UnityText-Regular.woff2'

interface PdfFontFace {
  family: string
  weight: number
  url: string
}

/**
 * Weight -> file mapping mirrored from `src/assets/css/_typography.tokens.scss`.
 * Several weights deliberately point at the same file (600 and 700 both
 * resolve to the -Medium face). html-to-image's `cloneCSSStyle` copies
 * *computed* styles onto its clone, so this set has to cover every numeric
 * `font-weight` the offscreen template can resolve to, not just "the 6 faces".
 */
export const PDF_FONT_FACES: readonly PdfFontFace[] = [
  { family: 'TCCC-UnityCondensed', weight: 600, url: UnityCondensedMedium },
  { family: 'TCCC-UnityCondensed', weight: 700, url: UnityCondensedMedium },
  { family: 'TCCC-UnityCondensed', weight: 800, url: UnityCondensedBold },
  { family: 'TCCC-UnityHeadline', weight: 600, url: UnityHeadlineMedium },
  { family: 'TCCC-UnityHeadline', weight: 700, url: UnityHeadlineMedium },
  { family: 'TCCC-UnityHeadline', weight: 800, url: UnityHeadlineBold },
  { family: 'TCCC-UnityText', weight: 400, url: UnityTextRegular },
  { family: 'TCCC-UnityText', weight: 500, url: UnityTextRegular },
  { family: 'TCCC-UnityText', weight: 600, url: UnityTextMedium },
  { family: 'TCCC-UnityText', weight: 700, url: UnityTextMedium },
]

const BASE64_CHUNK = 0x8000

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += BASE64_CHUNK) {
    // Chunked so String.fromCharCode never blows its argument-count limit.
    binary += String.fromCharCode(...bytes.subarray(offset, offset + BASE64_CHUNK))
  }
  return window.btoa(binary)
}

/** url -> base64, so `TCCC-UnityCondensed-Medium.woff2` is fetched once for two faces. */
const fontBase64Cache = new Map<string, Promise<string>>()

const fetchFontBase64 = (url: string): Promise<string> => {
  const cached = fontBase64Cache.get(url)
  if (cached) return cached

  const pending = fetch(url, { mode: 'cors', credentials: 'omit' })
    .then(response => {
      if (!response.ok) throw new Error(`Font fetch failed (${response.status}): ${url}`)
      return response.arrayBuffer()
    })
    .then(arrayBufferToBase64)
    .catch(error => {
      fontBase64Cache.delete(url) // let the next export retry
      throw error
    })

  fontBase64Cache.set(url, pending)
  return pending
}

/** Session-level cache of the assembled CSS: the 6 files are fetched once, ever. */
let fontEmbedCssPromise: Promise<string> | null = null

/**
 * `@font-face` CSS with every face inlined as a base64 data URI, for
 * html-to-image's `fontEmbedCSS` option.
 *
 * Passing this explicitly (rather than letting html-to-image auto-embed)
 * matters for two reasons: its auto-embed walks `document.styleSheets`, which
 * on this single-spa shell page also holds cross-origin sheets that throw on
 * `.cssRules` and trigger a re-fetch-by-href fallback; and the SVG
 * `foreignObject` the raster is produced from cannot load external resources
 * at all, so a `url(https://...)` reference inside it silently resolves to
 * nothing regardless.
 */
export const getPdfFontEmbedCss = (): Promise<string> => {
  if (fontEmbedCssPromise) return fontEmbedCssPromise

  fontEmbedCssPromise = Promise.all(
    PDF_FONT_FACES.map(async face => {
      const base64 = await fetchFontBase64(face.url)
      return [
        '@font-face {',
        `  font-family: '${face.family}';`,
        '  font-style: normal;',
        `  font-weight: ${face.weight};`,
        '  font-display: block;',
        `  src: url("data:font/woff2;charset=utf-8;base64,${base64}") format("woff2");`,
        '}',
      ].join('\n')
    }),
  )
    .then(blocks => blocks.join('\n'))
    .catch(error => {
      fontEmbedCssPromise = null
      throw error
    })

  return fontEmbedCssPromise
}

/**
 * Forces the browser to actually download and activate every face used by the
 * template, so the auto-fit pass measures real metrics rather than the
 * fallback font.
 *
 * `document.fonts.ready` on its own is not sufficient: it resolves once no
 * font load is *pending*, and a face nothing has requested yet is never
 * pending -- on a page that has never rendered UnityCondensed it resolves
 * immediately and reports done when nothing has loaded. `load()` first,
 * `ready` second.
 */
export const waitForPdfFonts = async (sampleText: string): Promise<void> => {
  if (!('fonts' in document)) return
  const text = sampleText.trim() || 'ABCabc123'
  await Promise.all(
    PDF_FONT_FACES.map(face =>
      document.fonts.load(`${face.weight} 16px '${face.family}'`, text).catch(() => undefined),
    ),
  )
  await document.fonts.ready
}
